import {memo} from 'react';
import type {PatternDetectResult} from '../../../../types/patternRecognition';
import type {AIAnalysis, SignalDescription} from '../types';
import {TREND_DESCRIPTIONS} from '../config/signalDescriptions';
import {
    calcKlineGap,
    formatRelativeTime,
    formatSignalTime,
    formatTimeTooltip,
    getSignalBorderClass,
    parseCreatedAt
} from '../utils/patternTime';
import {AiAnalysisReport} from './AiAnalysisReport';
import {SignalDescriptionBlock} from './SignalDescriptionBlock';

interface TrendPatternCardProps {
    pattern: PatternDetectResult;
    selected: boolean;
    viewChartActive: boolean;
    onSelect: () => void;
    onViewChart: () => void;
    isAiAnalyzing: boolean;
    aiAnalysis?: AIAnalysis;
    isAiExpanded: boolean;
    onAiClick: () => void;
    canTriggerAi: boolean;
    isPocPage: boolean;
    interval?: string;
    requiredCandleCount?: number;
    quickTradeEnabled: boolean;
    validationError?: string | null;
    expiryReason?: string | null;
    onGoTrade?: (direction: 'FORWARD' | 'REVERSE') => void;
}

function directionLabel(dir: number): string {
    if (1 === dir) return '看多';
    if (-1 === dir) return '看空';
    return '中性';
}

function directionClass(dir: number): string {
    if (1 === dir) return 'bull';
    if (-1 === dir) return 'bear';
    return 'neutral';
}

function getCategoryLabel(category?: string | null): string {
    if (!category) return '趋势';
    switch (category) {
        case 'REVERSAL':
            return '反转';
        case 'CONTINUATION':
            return '持续';
        case 'BILATERAL':
            return '双边';
        default:
            return category;
    }
}

const categoryColor: Record<string, string> = {
    REVERSAL: 'var(--pt-amber)',
    CONTINUATION: 'var(--pt-bull)',
    BILATERAL: 'var(--pt-blue)',
};

const categoryBg: Record<string, string> = {
    REVERSAL: 'var(--pt-amber-dim)',
    CONTINUATION: 'var(--pt-bull-dim)',
    BILATERAL: 'var(--pt-blue-dim)',
};

export const TrendPatternCard = memo(function TrendPatternCard({
                                                                   pattern,
                                                                   selected,
                                                                   viewChartActive,
                                                                   onSelect,
                                                                   onViewChart,
                                                                   isAiAnalyzing,
                                                                   aiAnalysis,
                                                                   isAiExpanded,
                                                                   onAiClick,
                                                                   canTriggerAi,
                                                                   isPocPage,
                                                                   interval,
                                                                   requiredCandleCount,
                                                                   quickTradeEnabled,
                                                                   validationError,
                                                                   expiryReason,
                                                                   onGoTrade,
                                                               }: TrendPatternCardProps) {
    const desc: SignalDescription | undefined = pattern.patternCode
        ? TREND_DESCRIPTIONS[pattern.patternCode]
        : undefined;
    const hasData = !!aiAnalysis;
    const recognizedAt = parseCreatedAt(pattern.createdAt);
    const timeGap = calcKlineGap(pattern.klineEndTime, Date.now(), interval);
    const borderClass = getSignalBorderClass(timeGap);
    const cat = pattern.trendCategory ?? '';
    const catLabel = getCategoryLabel(pattern.trendCategory);

    return (
        <div
            className={`pattern-card${selected ? ' selected' : ''}${-1 === pattern.direction ? ' bear-card' : ''}${borderClass ? ' ' + borderClass : ''}`}
            onClick={onSelect}
        >
            <div className="pc-header">
                <div className="pc-name">{pattern.patternName}</div>
                <div className={`pc-dir ${directionClass(pattern.direction)}`}>
                    {directionLabel(pattern.direction)}
                </div>
            </div>

            <div className="pc-meta">
                {cat && (
                    <span style={{
                        fontSize: 10, fontFamily: 'var(--pt-font-mono)', fontWeight: 600,
                        padding: '2px 7px', borderRadius: 2, display: 'inline-block',
                        background: categoryBg[cat] ?? 'var(--pt-text-dim)',
                        color: categoryColor[cat] ?? 'var(--pt-text-dim)',
                        border: `1px solid ${(categoryColor[cat] ?? 'var(--pt-text-dim)')}33`,
                    }}>
                        {catLabel}
                    </span>
                )}
                <div className="pc-conf">
                    <span>置信度</span>
                    <div className="conf-bar">
                        <div
                            className={`conf-fill ${directionClass(pattern.direction)}`}
                            style={{width: `${pattern.confidence}%`}}
                        />
                    </div>
                    <span style={{color: `var(--pt-${directionClass(pattern.direction)})`}}>
                        {pattern.confidence}%
                    </span>
                </div>
            </div>

            <div className="pc-time">
                <div>形成区间 {formatSignalTime(pattern.klineStartTime)} → {formatSignalTime(pattern.klineEndTime)}</div>
                <div style={{fontSize: 10, color: 'var(--pt-text-dim)'}}>
                    本次 {pattern.klineCount} 根K线
                    {null == requiredCandleCount ? '' : ` · 最低要求 ${requiredCandleCount} 根`}
                </div>
                <div style={{fontSize: 10, color: 'var(--pt-text-dim)'}}
                     title={null == recognizedAt ? undefined : formatTimeTooltip(recognizedAt)}>
                    识别于 {null == recognizedAt ? '--' : formatSignalTime(recognizedAt)}
                    {null == recognizedAt ? '' : ` · ${formatRelativeTime(recognizedAt)}`}
                </div>
                {pattern.breakoutPrice && (
                    <div style={{fontSize: 10, marginTop: 2}}>
                        突破价 {pattern.breakoutPrice.toFixed(2)}
                    </div>
                )}
            </div>

            {desc && <SignalDescriptionBlock description={desc}/>}

            <button
                type="button"
                className={`pc-view-chart-btn${viewChartActive ? ' active' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    onViewChart();
                }}
            >
                在图表中查看
            </button>

            {!isPocPage || hasData || pattern.hasAiAnalysis ? (
                <div className="pc-ai-section">
                    <button
                        className={`pc-ai-btn${isAiAnalyzing ? ' loading' : ''}${hasData || pattern.hasAiAnalysis ? ' has-data' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAiClick();
                        }}
                        disabled={isAiAnalyzing || !canTriggerAi}
                        title={isAiAnalyzing ? 'AI 分析中...' : hasData ? '查看AI解读' : '触发AI分析'}
                    >
                        {isAiAnalyzing ? '⏳' : hasData ? '📋' : '🤖'}
                    </button>
                </div>
            ) : null}

            {isAiExpanded && aiAnalysis && (
                <AiAnalysisReport
                    aiData={aiAnalysis}
                    holdingPeriodText={aiAnalysis.holdingPeriodText}
                    onGoTrade={onGoTrade}
                    quickTradeEnabled={quickTradeEnabled}
                    validationError={validationError}
                    expiryReason={expiryReason}
                    signalDirection={pattern.direction as 1 | -1 | 0}
                    signalConfidence={pattern.confidence}
                />
            )}
            {isAiExpanded && isAiAnalyzing && <div className="pc-ai-report">AI 分析中...</div>}
        </div>
    );
});
